import { z } from 'zod';

import { SOCIAL_PROVIDERS } from '../social/outcome';
import { SIGNUP_STEPS } from './steps';

/**
 * What the `/signup` URL is allowed to say.
 *
 * The signup route is a public front door, so the URL that reaches it is whatever a stale
 * bookmark, a mangled email link or a hand-typed guess happens to carry. Every optional param
 * therefore `catch`es rather than throws: a value this schema does not recognise is dropped, and
 * the route falls back to the first screen. Before that, `?step=company` threw the raw zod
 * failure into the router error card, stack trace and all (ux-audit finding #6).
 *
 * Dropping a bad value is safe because nothing here is a permission: `steps.ts` still decides
 * whether the step a *valid* value asks for is reachable with the evidence in `sessionStorage`.
 */
/** Why login sent someone to the address form instead of into the app. */
export const SIGNUP_EMAIL_REASONS = ['unverified', 'email-missing'] as const;
export type SignupEmailReason = (typeof SIGNUP_EMAIL_REASONS)[number];

export const signupSearchSchema = z.object({
	// For invited signups the email arrives base64-encoded from the invite link;
	// trimming/lower-casing it corrupts the base64 (VAD-677). Keep it verbatim and
	// decode it where it's consumed.
	email: z.string().optional(),
	companyID: z.string().optional(),
	// Which of the signup screens to show. Absent - or unrecognised - means the first one.
	// `details` finishes an unfinished social signup, `link-proof` proves the mailbox before an
	// automatic link, `email` restarts the email path.
	step: z.enum(SIGNUP_STEPS).optional().catch(undefined),
	// Login sends the person here when a provider would not vouch for their address, or shared no
	// address at all; the email step explains why they are typing an address rather than finishing
	// the tap they started. Two callers say the unverified case two ways - the signup entry rule
	// sets the flag, the login screen names a reason - so both are accepted.
	unverified: z.boolean().optional().catch(undefined),
	reason: z.enum(SIGNUP_EMAIL_REASONS).optional().catch(undefined),
	// Which provider the tap that sent the person here was for, so the notice names it rather than
	// saying "your provider". Absent when the journey did not start at a provider button.
	provider: z.enum(SOCIAL_PROVIDERS).optional().catch(undefined),
});

export type SignupSearch = z.infer<typeof signupSearchSchema>;

/** The one reading of those two params, so the route and the step never disagree about the notice. */
export const emailStepReason = (search: SignupSearch): SignupEmailReason | null => {
	if (search.reason) return search.reason;
	return search.unverified === true ? 'unverified' : null;
};
